/**
 * LA ÚNICA PUERTA PARA CONFIRMAR UN MUESTREO CON FOCOS.
 *
 * Confirmar = plaga + gravedad + avance + foco, todo o nada, en una sola RPC.
 * Este fichero es el único del cliente que nombra esa RPC (lo vigila
 * `confirmOnePath.guard`): las tres pantallas donde se confirma —panel de
 * triaje, resumen del asistente de captura y vista previa de voz— pasan por
 * aquí, así que ninguna puede enlazar de otra manera ni saltarse la gravedad.
 *
 * NUNCA LANZA. Devuelve `{ ok: false, error }` con el error ya traducido a «qué
 * hace la pantalla» (`confirmErrors.ts`): un `legacy` significa que esta empresa
 * o esta base no tienen focos, y quien llama sigue por el camino de siempre.
 */
import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidateScoutingData } from "@/lib/scoutingDataInvalidation";
import { focusErrorOf, type FocusError } from "@/lib/pests/confirmErrors";
import { THRESHOLDS_VERSION, type Candidate } from "@/lib/health/focusLinking";
import type { ClosedFocusChoice, ConfirmMode } from "@/lib/pests/confirmForm";

export type ConfirmSeverity = "low" | "medium" | "high" | "critical";
export type ConfirmVerdict = "better" | "same" | "worse";

export interface ConfirmInput {
  sessionId: string;
  pestId: string | null;
  /**
   * El nombre tal como lo ve quien confirma (traducido), que es lo que el camino
   * antiguo guardaba como instantánea. `null` ⇒ la base pone el del catálogo.
   */
  pestName: string | null;
  severity: ConfirmSeverity;
  verdict: ConfirmVerdict | null;
  focusAction: "new" | "join" | "none";
  focusId: string | null;
  /** «Es el mismo foco»: este muestreo adopta la plaga del foco. */
  adoptFocusPest: boolean;
  /** Los focos que se enseñaron: si apareció otro entretanto, la base avisa en vez de duplicar. */
  seenFocusIds: string[];
  /** El mismo en los reintentos: un doble toque no crea dos focos. */
  clientRequestId: string;
  mode: ConfirmMode;
  candidates: readonly Candidate[];
  proposalKind: string;
  /**
   * D: el muestreo es de ANTES de que se cerrara el foco. «reopen» lo reabre con él;
   * «keep_closed» (y la ausencia, en bundles viejos) lo hace entrar ya cerrado.
   */
  closedFocus?: ClosedFocusChoice | null;
}

export interface ConfirmResult {
  sessionId: string;
  focusId: string | null;
  focusNumber: number | null;
  createdFocus: boolean;
  reopened: boolean;
  /** Entró en un foco ya cerrado, cerrado él también (D). */
  joinedClosed: boolean;
  inheritedTaskId: string | null;
}

/**
 * Los dos campos SIEMPRE presentes (uno de ellos `null`): el proyecto compila sin
 * `strictNullChecks`, y ahí una unión discriminada por un booleano no estrecha.
 */
export type ConfirmOutcome = { ok: true; result: ConfirmResult; error: null } | { ok: false; result: null; error: FocusError };

const RPC = "confirm_scouting_session";
const RETRY_DELAY_MS = 350;
const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Lo que se propuso, en corto: los tres primeros candidatos con sus distancias. */
const proposalOf = (input: ConfirmInput) => ({
  thresholds: THRESHOLDS_VERSION,
  kind: input.proposalKind,
  top: input.candidates.slice(0, 3).map((c) => ({
    focus_id: c.focus.id,
    tier: c.tier,
    reasons: c.reasons,
    line_gap: c.lineGap,
    along_gap_m: c.alongGapM == null ? null : Math.round(c.alongGapM * 10) / 10,
    days_since_last: c.daysSinceLast,
    days_since_resolved: c.daysSinceResolved,
    hinted: c.hinted,
  })),
});

export function useConfirmScoutingSession() {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const confirm = useCallback(
    async (input: ConfirmInput): Promise<ConfirmOutcome> => {
      const p_payload = {
        session_id: input.sessionId,
        pest_id: input.pestId,
        pest_name: input.pestName,
        severity: input.severity,
        evolution_verdict: input.verdict,
        focus_action: input.focusAction,
        focus_id: input.focusAction === "join" ? input.focusId : null,
        adopt_focus_pest: input.focusAction === "join" && input.adoptFocusPest,
        seen_focus_ids: input.seenFocusIds,
        client_request_id: input.clientRequestId,
        mode: input.mode,
        proposal: proposalOf(input),
        closed_focus: input.focusAction === "join" ? input.closedFocus ?? null : null,
      };
      setBusy(true);
      try {
        // Otra escritura del mismo sector tenía el candado: un reintento, y no más.
        for (let attempt = 0; ; attempt++) {
          const { data, error } = await supabase.rpc(RPC, { p_payload });
          if (!error) {
            const r = (data ?? {}) as Record<string, unknown>;
            return {
              ok: true,
              error: null,
              result: {
                sessionId: input.sessionId,
                focusId: (r.focus_id as string | null) ?? null,
                focusNumber: (r.focus_number as number | null) ?? null,
                createdFocus: r.created_focus === true,
                reopened: r.reopened === true,
                joinedClosed: r.joined_closed === true,
                inheritedTaskId: (r.inherited_task_id as string | null) ?? null,
              },
            };
          }
          const mapped = focusErrorOf(error);
          if (mapped.kind === "retry" && attempt === 0) {
            await wait(RETRY_DELAY_MS);
            continue;
          }
          return { ok: false, result: null, error: mapped };
        }
      } catch (e) {
        return { ok: false, result: null, error: focusErrorOf(e) };
      } finally {
        setBusy(false);
        invalidateScoutingData(queryClient);
      }
    },
    [queryClient],
  );

  return { confirm, busy };
}
